import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CalendarClock, Loader2 } from "lucide-react";

/**
 * Force-Profile DOB modal — shown on /student mount when a student has
 * `date_of_birth == null`. Cannot be dismissed until DOB is saved.
 */
export default function ForceProfileDob() {
  const { user, refresh } = useAuth();
  const [dob, setDob] = useState("");
  const [saving, setSaving] = useState(false);

  const needsDob =
    user &&
    user.role === "student" &&
    user.status === "approved" &&
    !user.date_of_birth;

  if (!needsDob) return null;

  const save = async () => {
    if (!dob) {
      toast.error("Please enter your date of birth");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/auth/me", { date_of_birth: dob });
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      const d = err?.response?.data?.detail || "Failed to save";
      toast.error(typeof d === "string" ? d : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open modal onOpenChange={() => {}}>
      <DialogContent
        className="max-w-md [&>button]:hidden"
        data-testid="force-dob-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-amber-600" />
            Complete your profile
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-stone-600">
            We need your date of birth to personalise learning content to your
            age. This is a one-time step.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="force-dob">Date of birth</Label>
            <Input
              id="force-dob"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
              data-testid="force-dob-input"
            />
          </div>
          <Button
            onClick={save}
            disabled={saving || !dob}
            className="w-full bg-stone-900 hover:bg-stone-800 text-white"
            data-testid="force-dob-save"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…
              </>
            ) : (
              "Save and continue"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
